import React, { useState, useEffect } from "react";
import { Typography, Tooltip } from "antd";

interface TimelineProps {
    client_start_time?: number;
    server_start_time?: number;
    request_start_time?: number;
    first_token_time?: number;
    request_end_time?: number;
    server_end_time?: number;
    client_end_time?: number;
}

interface TimeSegment {
    id: string;
    label: string;
    startTime: number;
    endTime: number;
    duration: number;
    color: string;
}

interface SummarySegment {
    id: string;
    label: string;
    startOffset: number;
    duration: number;
    color: string;
}

const RequestTimeline: React.FC<TimelineProps> = ({
    client_start_time,
    server_start_time,
    request_start_time,
    first_token_time,
    request_end_time,
    server_end_time,
    client_end_time,
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    if (!client_start_time || !client_end_time) return null;

    const roundTime = (time?: number) => {
        if (!time) return 0;
        return Math.round(time);
    };

    const baseTime = client_start_time;
    
    const normalizeTime = (time?: number) => {
        if (!time) return 0;
        return roundTime(time - baseTime);
    };

    const totalDuration = normalizeTime(client_end_time);

    // Calculate minimum width percentage (3px minimum width)
    const getMinWidthPercentage = (duration: number) => {
        const calculatedWidth = (duration / totalDuration) * 100;
        return duration > 0 ? Math.max(calculatedWidth, 2) : 0;
    };

    // Calculate all segment durations
    const clientToServerDuration = normalizeTime(server_start_time) - normalizeTime(client_start_time);
    const serverToRequestDuration = normalizeTime(request_start_time) - normalizeTime(server_start_time);
    const requestDuration = normalizeTime(request_end_time) - normalizeTime(request_start_time);
    const requestToFirstTokenDuration = first_token_time ? normalizeTime(first_token_time) - normalizeTime(request_start_time) : 0;
    const requestToServerEndDuration = normalizeTime(server_end_time) - normalizeTime(request_end_time);
    const serverToClientDuration = normalizeTime(client_end_time) - normalizeTime(server_end_time);
    const serverTotalDuration = normalizeTime(server_end_time) - normalizeTime(server_start_time);
    const networkTotalDuration = clientToServerDuration + serverToClientDuration;

    // Create horizontally stacked segments
    const stackedSegments: TimeSegment[] = [
        {
            id: "client_to_server",
            label: "Client → Server",
            startTime: 0,
            endTime: clientToServerDuration,
            duration: clientToServerDuration,
            color: "#4682B4" // SteelBlue
        },
        {
            id: "server_to_request",
            label: "Server → Request",
            startTime: clientToServerDuration,
            endTime: clientToServerDuration + serverToRequestDuration,
            duration: serverToRequestDuration,
            color: "#66CDAA" // MediumAquamarine
        },
        {
            id: "request_duration",
            label: "Request",
            startTime: clientToServerDuration + serverToRequestDuration,
            endTime: clientToServerDuration + serverToRequestDuration + requestDuration,
            duration: requestDuration,
            color: "#FF7F50" // Coral
        },
        {
            id: "request_to_server_end",
            label: "Request → Server End",
            startTime: clientToServerDuration + serverToRequestDuration + requestDuration,
            endTime: clientToServerDuration + serverToRequestDuration + requestDuration + requestToServerEndDuration,
            duration: requestToServerEndDuration,
            color: "#9370DB" // MediumPurple
        },
        {
            id: "server_to_client",
            label: "Server → Client",
            startTime: clientToServerDuration + serverToRequestDuration + requestDuration + requestToServerEndDuration,
            endTime: totalDuration,
            duration: serverToClientDuration,
            color: "#4682B4" // SteelBlue
        }
    ].filter(segment => segment.duration > 0);

    // Create summary segments (positioned bars)
    const summarySegments: SummarySegment[] = [
        {
            id: "total",
            label: "Total",
            startOffset: 0,
            duration: totalDuration,
            color: "#7B68EE" // SlateBlue
        },
        {
            id: "network",
            label: "Network",
            startOffset: 0,
            duration: networkTotalDuration,
            color: "#4682B4" // SteelBlue
        },
        {
            id: "server",
            label: "Server",
            startOffset: clientToServerDuration,
            duration: serverTotalDuration,
            color: "#20B2AA" // LightSeaGreen
        },
        {
            id: "request",
            label: "Request",
            startOffset: clientToServerDuration + serverToRequestDuration,
            duration: requestDuration,
            color: "#FF7F50" // Coral
        },
        ...(first_token_time ? [{
            id: "ttft",
            label: "TTFT",
            startOffset: clientToServerDuration + serverToRequestDuration,
            duration: requestToFirstTokenDuration,
            color: "#FFD700" // Gold
        }] : [])
    ];

    return (
        <div style={{ 
            width: "100%", 
            padding: isMobile ? "8px" : "16px", 
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            boxSizing: "border-box",
            overflow: "hidden"
        }}>
            <Typography.Title level={5} style={{ 
                margin: isMobile ? "0 0 16px 0" : "0 0 24px 0", 
                fontSize: isMobile ? "14px" : "16px" 
            }}>
                Request Timeline
            </Typography.Title>
            
            {/* Stacked Bar */}
            <div style={{ marginBottom: isMobile ? "20px" : "32px" }}>
                <div style={{ 
                    display: "flex",
                    height: isMobile ? "20px" : "24px",
                    width: "100%",
                    borderRadius: "4px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                }}>
                    {stackedSegments.map(segment => (
                        <Tooltip 
                            key={segment.id}
                            title={
                                <div>
                                    <div><strong>{segment.label}</strong></div>
                                    <div>Duration: {segment.duration} ms</div>
                                </div>
                            }
                        >
                            <div 
                                style={{
                                    width: `${getMinWidthPercentage(segment.duration)}%`,
                                    height: "100%",
                                    backgroundColor: segment.color,
                                    cursor: "pointer",
                                    transition: "opacity 0.15s ease",
                                    opacity: 0.9
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
                            />
                        </Tooltip>
                    ))}
                </div>
            </div>
            
            {/* Summary Bars */}
            <div>
                {summarySegments.map(segment => (
                    <div key={segment.id} style={{ 
                        marginBottom: isMobile ? "12px" : "16px",
                        display: "flex",
                        alignItems: "center",
                        height: isMobile ? "20px" : "24px"
                    }}>
                        <div style={{ 
                            width: isMobile ? "60px" : "80px",
                            marginRight: isMobile ? "8px" : "12px",
                            fontSize: isMobile ? "12px" : "13px",
                            color: "#666"
                        }}>
                            {segment.label}
                        </div>
                        
                        <div style={{ 
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0 // This prevents flex items from overflowing
                        }}>
                            <div style={{ 
                                position: "relative",
                                height: isMobile ? "12px" : "16px",
                                width: "100%",
                                backgroundColor: "#f5f5f5",
                                borderRadius: isMobile ? "6px" : "8px",
                                overflow: "hidden"
                            }}>
                                <Tooltip 
                                    title={`${segment.duration}ms (${((segment.duration / totalDuration) * 100).toFixed(1)}% of total)`}
                                >
                                    <div style={{ 
                                        position: "absolute",
                                        top: 0,
                                        left: `${(segment.startOffset / totalDuration) * 100}%`,
                                        height: "100%",
                                        width: `${getMinWidthPercentage(segment.duration)}%`,
                                        backgroundColor: segment.color,
                                        cursor: "pointer",
                                        borderRadius: isMobile ? "6px" : "8px",
                                        transition: "opacity 0.15s ease",
                                        opacity: 0.9
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                        
                        <div style={{ 
                            marginLeft: isMobile ? "6px" : "12px",
                            width: isMobile ? "48px" : "56px",
                            fontSize: isMobile ? "11px" : "12px",
                            textAlign: "right",
                            color: "#666",
                            whiteSpace: "nowrap"
                        }}>
                            {segment.duration}ms
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RequestTimeline; 